/* eslint-disable camelcase */
import React, { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Row, Col } from 'react-bootstrap';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import DrawerWrapper, { DrawerFooter } from '../../DrawerWrapper';
import FormSelectField from '../../formFields/FormSelectField';
import FormTextAreaField from '../../formFields/FormTextAreaField';
import FormDateTimeField from '../../formFields/FormDateTimeField';
import FormGeoLocationField from '../../formFields/FormGeoLocationField';
import {
  ResourceInIncident,
  useUpdateResourceWorkAssignments,
} from '../../hooks/resourceInIncidentHooks';
import { useWorkAssignments } from '../../hooks/workAssignmentHooks';
import { useResourceSites } from '../../hooks/resourceSiteHooks';
import { Form } from 'react-bootstrap';
import RelationshipPickerField from '../../formFields/RelationshipPickerField';
import { WorkAssignment } from '../../hooks/workAssignmentHooks';
import { getStatusColor, STATUS_SELECT_OPTIONS } from '../utils/statusUtils';
import { STATUS_CONFIG, ResourceStatus } from '../constants/resourceConstants';

interface StatusUpdateDrawerProps {
  resource: ResourceInIncident;
  onClose: () => void;
  onSave: (data: Partial<ResourceInIncident>) => void;
}

interface StatusUpdateFormData {
  currentStatus: ResourceStatus;
  workAssignmentIds: string[];
  useCustomLocation?: boolean;
  resourceSiteId?: string;
  locationGeolocation?: {
    lat?: number;
    lng?: number;
    name?: string;
    address?: string;
  } | null;
  estimatedReturnTime?: string;
  statusNotes?: string;
}

const StatusUpdateDrawer: React.FC<StatusUpdateDrawerProps> = ({
  resource,
  onClose,
  onSave,
}) => {
  const { data: workAssignments = [] } = useWorkAssignments();
  const { data: resourceSites = [] } = useResourceSites();
  const updateWorkAssignmentsMutation = useUpdateResourceWorkAssignments();

  // Find work assignments that currently contain this resource
  const resourceSetId = resource.resource_set_id;
  const currentWorkAssignmentIds = workAssignments
    .filter((wa) => wa.resource_sets?.includes(resourceSetId))
    .map((wa) => wa.work_assignment_id);

  const form = useForm<StatusUpdateFormData>({
    mode: 'onChange',
    defaultValues: {
      currentStatus: resource.current_status || 'available',
      workAssignmentIds: currentWorkAssignmentIds,
      useCustomLocation: !!resource.assignment_geolocation,
      resourceSiteId: resource.assignment_site_id || '',
      locationGeolocation: resource.assignment_geolocation || null,
      estimatedReturnTime: resource.estimated_return_time || '',
      statusNotes: resource.status_notes || '',
    },
  });

  const { handleSubmit, watch } = form;
  const currentStatus = watch('currentStatus');
  const useCustomLocation = watch('useCustomLocation');

  const resourceSiteOptions = useMemo(
    () =>
      resourceSites.map((site) => ({
        value: site.id,
        label: site.name,
      })),
    [resourceSites]
  );

  const onSubmit = async (data: StatusUpdateFormData) => {
    const updateData: Partial<ResourceInIncident> = {
      current_status: data.currentStatus,
      // Nullify ETR for assigned and available statuses
      estimated_return_time:
        data.currentStatus === 'assigned' || data.currentStatus === 'available'
          ? undefined
          : data.estimatedReturnTime || undefined,
      status_notes: data.statusNotes,
      status_since: new Date().toISOString(),
      last_status_update_datetime: new Date().toISOString(),
    };

    // Handle location based on toggle
    if (data.useCustomLocation && data.locationGeolocation) {
      // Set assignment location fields for proper syncing to org resource
      updateData.assignment_geolocation = data.locationGeolocation;
      updateData.incident_assignment_location =
        data.locationGeolocation.name ||
        `${data.locationGeolocation.lat}, ${data.locationGeolocation.lng}`;
    } else if (data.resourceSiteId) {
      // Set assignment site for proper syncing
      updateData.assignment_site_id = data.resourceSiteId;
      const site = resourceSites.find((s) => s.id === data.resourceSiteId);
      if (site) {
        updateData.incident_assignment_location = site.name;
      }
    }

    // Update work assignments if they've changed
    const dataSet = new Set(data.workAssignmentIds);
    const currentSet = new Set(currentWorkAssignmentIds);
    const hasWorkAssignmentChanges =
      dataSet.size !== currentSet.size ||
      [...dataSet].some((id) => !currentSet.has(id));

    if (hasWorkAssignmentChanges) {
      await updateWorkAssignmentsMutation.mutateAsync({
        id: resource.id,
        workAssignmentIds: data.workAssignmentIds,
      });
    }

    onSave(updateData);
  };

  return (
    <DrawerWrapper toggle={onClose} title="Update Resource Status (T-Card)">
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-4">
            <h5 className="mb-3">Status Update</h5>

            <Row className="mb-3">
              <Col md={12}>
                <FormSelectField
                  fieldName="currentStatus"
                  title="Resource Status"
                  options={STATUS_SELECT_OPTIONS}
                  rules={{
                    required: { value: true, message: 'Status is required' },
                  }}
                />
                <div
                  className={`mt-2 badge bg-${getStatusColor(currentStatus)}`}
                >
                  Current:{' '}
                  {STATUS_CONFIG[currentStatus]?.label || currentStatus}
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <RelationshipPickerField<WorkAssignment>
                  label="Work Assignments"
                  fieldName="workAssignmentIds"
                  defaultValue={currentWorkAssignmentIds}
                  options={workAssignments}
                  identity={(wa) => {
                    return wa.work_assignment_id;
                  }}
                  title={(wa) => {
                    return wa.work_assignment || 'Unnamed Assignment';
                  }}
                  cardType="work-assignment"
                />
                {currentStatus === 'assigned' && (
                  <small className="text-muted mt-1 d-block">
                    Note: Resources with &apos;Assigned&apos; status should have
                    work assignments selected.
                  </small>
                )}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Check
                  type="switch"
                  id="use-custom-location"
                  label="Use Custom Location"
                  {...form.register('useCustomLocation')}
                  className="mb-3"
                />

                {useCustomLocation ? (
                  <FormGeoLocationField
                    name="locationGeolocation"
                    label="Custom Location"
                  />
                ) : (
                  <FormSelectField
                    fieldName="resourceSiteId"
                    title="Resource Site"
                    options={resourceSiteOptions}
                    isClearable
                  />
                )}
              </Col>
            </Row>

            {(currentStatus === 'os_rest' ||
              currentStatus === 'os_personnel' ||
              currentStatus === 'os_mechanical' ||
              currentStatus === 'etr') && (
              <Row className="mb-3">
                <Col md={12}>
                  <FormDateTimeField
                    fieldName="estimatedReturnTime"
                    title="Estimated Return Time"
                  />
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={12}>
                <FormTextAreaField
                  fieldName="statusNotes"
                  title="Status Notes"
                />
              </Col>
            </Row>

            <div className="alert alert-info">
              <small>
                <strong>T-Card System:</strong> This update simulates a physical
                T-Card movement. Status changes are tracked in the resource
                history per ICS 219.
              </small>
            </div>
          </div>

          <DrawerFooter>
            <StylishNewButton
              type="button"
              className="button button--link me-2"
              onClick={onClose}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton type="submit" className="button button--primary">
              Update Status
            </StylishNewButton>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default StatusUpdateDrawer;
